import { ERROR_MESSAGES } from '@/shared/constants/error-messages';
import MessageIcon from '@/assets/icons/message-fill-primary.svg';
import * as Yup from 'yup';
import { ICustomersLoginResponse } from "@/shared/endpoints/customers"
import { useFormik } from 'formik';
import { Button, Input } from '@/shared/ui';
import { useState, useMemo } from 'react';
import NoEmailPopup from './no-email';
import { useFormikWrapper } from '@/shared/hooks/formikWrapper';

const initialValues = {
    email: ''
};
const unsupportedEmailProviders = ['icloud.com', 'mac.com', 'me.com'];
const validationSchema = Yup.object().shape({
    email: Yup.string()
        .required(ERROR_MESSAGES.REQUIRED_FIELD_MISSING)
        .email(ERROR_MESSAGES.VALID_EMAIL)
        .test(
            'unsupported-email-provider',
            "Sorry, we're unable to send emails to addresses from your email provider. Please use a different email address to continue.",
            value => {
                if (value) {
                    const domain = value.split('@')[1];
                    return !unsupportedEmailProviders.includes(domain);
                }
                return true;
            }
        ),
});

interface IProps {
    email?: string;
    loading?: boolean;
    onSubmit(email: string): void;
    userData?: ICustomersLoginResponse | null;
}

const SetEmailPopup = ({ email, loading, onSubmit, userData }: IProps) => {
    const [noEmailOpen, setNoEmailOpen] = useState(false);

    const formik = useFormikWrapper({
        initialValues: { ...initialValues, email: email || '' },
        validationSchema,
        onSubmit: (values) => {
            onSubmit(values.email)
        }
    });

    const textChangesBasedOnEmailVerifiedRes = useMemo(() => {
        return {
            header: userData?.emailVerified ? `Enter your email` : `Set up your email`,
            subHeader: userData?.emailVerified
                ? `Please enter the email associated with your MedBox account.`
                : `Add the email you want to use for logging into your MedBox account.`,
            proceedButtonText: userData?.emailVerified ? 'Continue' : 'Set Email'
        }
    }, [userData]);

    if (noEmailOpen) return <NoEmailPopup onOkay={() => setNoEmailOpen(false)} />

    return (
        <form data-testid="set-password-flow-email-form-container" className="flex flex-col items-center" onSubmit={formik.handleSubmit}>
            <img width={43} src={MessageIcon} alt="message icon" className="w-[2.6875rem] md:w-[3.5rem] mb-2.5" />
            <h3 data-testid="set-password-flow-email-title" className="text-center text-[1.5625rem] md:text-[2.1875rem] font-agrandir font-normal leading-normal mb-4">{textChangesBasedOnEmailVerifiedRes.header}</h3>
            <p data-testid="set-password-flow-email-subtitle" className="text-center text-17 md:text-xl mb-4 font-dmSans">{textChangesBasedOnEmailVerifiedRes.subHeader}</p>

            <Input
                name="email"
                className="mb-4 md:mb-6"
                label="Email"
                value={formik.values.email}
                required
                placeholder="ex. janed@gmail.com"
                error={!!(formik.touched.email && formik.errors.email)}
                errorMessage={formik.errors.email}
                onChange={event => formik.setFieldValue('email', event.target.value)}
                labelClassName="font-dmSans"
                enableNaturalEnter
                testId="set-password-flow-email-input"
            />

            <Button
                loading={loading}
                className="w-full mb-4 md:mb-6"
                showEndIcon
                type="submit"
                testId="set-password-flow-email-continue-button"
                fontClassName='font-bold'
            >
                <span className="font-dmSans font-bold">{textChangesBasedOnEmailVerifiedRes.proceedButtonText}</span>
            </Button>

            <Button variant="text" onClick={() => setNoEmailOpen(true)} testId="set-password-flow-email-no-email-button">
                <span className="font-dmSans font-bold">I don't have an email</span>
            </Button>
        </form>
    );
}

export default SetEmailPopup;